import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { logAccess, AccessType } from '../lib/services/AccessLoggerService';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        setStatus('loading');
        setMessage('Authentification en cours...');

        // Vérifier si on est sur le mauvais domaine (redirection Supabase vers GOTRUE_SITE_URL)
        const storedOrigin = sessionStorage.getItem('oauth_origin');
        const currentOrigin = window.location.origin;
        
        if (storedOrigin && storedOrigin !== currentOrigin) {
          // On est sur le mauvais domaine, rediriger vers le bon domaine avec les paramètres de l'URL
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrl = `${storedOrigin}/auth/callback?${urlParams.toString()}`;
          
          console.log('🔄 Redirection détectée vers le mauvais domaine');
          console.log('🔍 Origine attendue:', storedOrigin);
          console.log('🔍 Origine actuelle:', currentOrigin);
          console.log('🔍 Redirection vers:', redirectUrl);
          
          // Rediriger vers le bon domaine avec tous les paramètres
          window.location.href = redirectUrl;
          return;
        }

        // Récupérer la session depuis l'URL
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          setStatus('error');
          setMessage('Erreur lors de l\'authentification. Veuillez réessayer.');
          return;
        }

        if (data?.session) {
          // Nettoyer sessionStorage après succès
          sessionStorage.removeItem('oauth_origin');
          sessionStorage.removeItem('oauth_redirect_url');
          
          // Enregistrer la connexion Google dans l'historique
          const email = data.session.user.email || 'Unknown';
          logAccess(AccessType.LOGIN, `Connexion réussie avec Google: ${email}`).catch(console.error);
          
          setStatus('success');
          setMessage('Connexion réussie ! Redirection...');
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1500);
        } else {
          setStatus('error');
          setMessage('Aucune session trouvée. Veuillez réessayer.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Une erreur inattendue s\'est produite.');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="w-8 h-8 animate-spin text-primary-500" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'error':
        return <XCircle className="w-8 h-8 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-primary-500';
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-white';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-2xl font-bold text-white">SF</span>
        </div>

        <div className="bg-dark-800 rounded-lg p-8 shadow-xl">
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
            {status === 'loading' && 'Authentification en cours...'}
            {status === 'success' && 'Connexion réussie !'}
            {status === 'error' && 'Erreur d\'authentification'}
          </h1>

          <p className="text-dark-400 mb-6">
            {message}
          </p>

          {status === 'error' && (
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors font-medium"
            >
              Retour à la connexion
            </button>
          )}

          {status === 'loading' && (
            <div className="flex justify-center">
              <div className="animate-pulse text-dark-400">
                Veuillez patienter...
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-dark-500">
            Si vous n'êtes pas automatiquement redirigé,{' '}
            <button
              onClick={() => navigate('/')}
              className="text-primary-400 hover:text-primary-300 transition-colors"
            >
              cliquez ici
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback; 